import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { IconLocation, Icons } from '../../../components';
import { getStrapiMediaUrl } from '../../../util/strapiApi';
import CommunityEventCard from '../../CommunityPage/CommunityEventCard';
import MarketplaceSectionHeader from './MarketplaceSectionHeader';
import {
  ALL_STATES_KEY,
  compareByFeaturedAndSortOrder,
  getStateKey,
  matchesSelectedState,
} from './communityStateFilter';
import css from './Section.module.css';

const ImageWithFallback = ({ src, alt, wrapperClassName, imageClassName, children }) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div className={classNames(css.imageFallback, wrapperClassName)}>
        <div className={css.imageFallbackFill} aria-label={alt}>
          <span className={css.imageFallbackLabel}>No image</span>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <img
        src={src}
        alt={alt}
        className={imageClassName}
        loading="lazy"
        onError={() => setHasError(true)}
      />
      {children}
    </div>
  );
};

const SkeletonEventCard = () => (
  <div className={css.skeletonCard}>
    <div className={classNames(css.skeletonBlock, css.skeletonImageWide)} />
    <div className={classNames(css.skeletonBlock, css.skeletonLine)} />
    <div className={classNames(css.skeletonBlock, css.skeletonLineShort)} />
    <div className={classNames(css.skeletonBlock, css.skeletonLine)} />
    <div className={classNames(css.skeletonBlock, css.skeletonButton)} />
  </div>
);

const formatEventDate = (startDateTime, endDateTime) => {
  if (!startDateTime) return 'Date TBA';

  const start = moment(startDateTime);
  if (!endDateTime) return start.format('MMM D, h:mm A');

  const end = moment(endDateTime);
  if (start.isSame(end, 'day')) {
    return `${start.format('MMM D, h:mm A')} - ${end.format('h:mm A')}`;
  }

  return `${start.format('MMM D, h:mm A')} - ${end.format('MMM D, h:mm A')}`;
};

const resolveEventState = event => {
  const relationState = event?.state && !Array.isArray(event.state) ? event.state : null;

  return {
    id: relationState?.id ?? event?.stateId ?? null,
    slug: relationState?.slug || event?.stateSlug || '',
    name: relationState?.name || event?.stateName || event?.locationState || '',
  };
};

function SectionPopularCollections(props) {
  const {
    events = [],
    isLoading,
    hasError,
    sectionId = 'events-by-state',
    title = 'Events by State',
    description = 'Browse upcoming mahjong events, classes, and meetups happening near you.',
    cardVariant = 'marketplace',
    headerAction = null,
  } = props;
  const history = useHistory();
  const [selectedStateKey, setSelectedStateKey] = useState(ALL_STATES_KEY);

  const orderedEvents = useMemo(() => {
    return [...events].filter(Boolean).sort((a, b) => {
      const aDate = a?.startDateTime
        ? new Date(a.startDateTime).getTime()
        : Number.MAX_SAFE_INTEGER;
      const bDate = b?.startDateTime
        ? new Date(b.startDateTime).getTime()
        : Number.MAX_SAFE_INTEGER;
      return aDate - bDate;
    });
  }, [events]);

  const orderedStates = useMemo(() => {
    const stateKeysWithEvents = new Set();

    orderedEvents.forEach(event => {
      const key = getStateKey(resolveEventState(event));
      if (key) {
        stateKeysWithEvents.add(key);
      }
    });

    const derivedStates = orderedEvents.map(resolveEventState).filter(state => getStateKey(state));
    const seen = new Set();

    return derivedStates.sort(compareByFeaturedAndSortOrder).filter(state => {
      const key = getStateKey(state);
      if (!key || seen.has(key) || !stateKeysWithEvents.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }, [orderedEvents]);

  const selectedState = useMemo(
    () => orderedStates.find(state => getStateKey(state) === selectedStateKey) || null,
    [orderedStates, selectedStateKey]
  );

  useEffect(() => {
    if (selectedStateKey === ALL_STATES_KEY) return;

    const hasSelectedState = orderedStates.some(state => getStateKey(state) === selectedStateKey);
    if (!hasSelectedState) {
      setSelectedStateKey(ALL_STATES_KEY);
    }
  }, [orderedStates, selectedStateKey]);

  const filteredEvents = useMemo(() => {
    if (selectedStateKey === ALL_STATES_KEY || !selectedState) {
      return orderedEvents;
    }

    return orderedEvents.filter(event =>
      matchesSelectedState(selectedState, resolveEventState(event))
    );
  }, [orderedEvents, selectedState, selectedStateKey]);

  const openEvent = slug => {
    if (!slug) return;
    history.push(`/events/${slug}`);
  };

  const showGlobalEmptyState = !isLoading && !hasError && orderedEvents.length === 0;
  const showFilteredEmptyState =
    !isLoading && !hasError && selectedStateKey !== ALL_STATES_KEY && filteredEvents.length === 0;
  const isCommunityCardVariant = cardVariant === 'community';
  const gridClassName = isCommunityCardVariant ? css.eventListingGrid : css.cardsGrid;

  return (
    <section id={sectionId} className={classNames(css.marketplaceSection, css.sectionPadding)}>
      <div className={css.fixedWidthContainer}>
        <MarketplaceSectionHeader
          title={title}
          description={description}
          headerAction={headerAction}
        />

        {isLoading ? (
          <>
            <div className={css.chipRow}>
              {Array.from({ length: 6 }).map((_, index) => (
                <span
                  key={`event-state-skeleton-${index}`}
                  className={classNames(css.skeletonBlock, css.skeletonPill)}
                />
              ))}
            </div>
            <div className={gridClassName}>
              {Array.from({ length: 4 }).map((_, index) => (
                <SkeletonEventCard key={`event-skeleton-${index}`} />
              ))}
            </div>
          </>
        ) : hasError ? (
          <div className={css.marketplaceEmptyState}>
            <h3>Unable to load events</h3>
            <p>Please refresh the page in a moment.</p>
          </div>
        ) : showGlobalEmptyState ? (
          <div className={css.marketplaceEmptyState}>
            <h3>No upcoming events yet</h3>
            <p>Create an event in your dashboard to help the community discover it.</p>
          </div>
        ) : (
          <>
            <div className={css.chipRow} role="toolbar" aria-label="Filter events by state">
              <button
                type="button"
                className={classNames(css.chip, {
                  [css.chipActive]: selectedStateKey === ALL_STATES_KEY,
                })}
                aria-pressed={selectedStateKey === ALL_STATES_KEY}
                onClick={() => setSelectedStateKey(ALL_STATES_KEY)}
              >
                All
              </button>

              {orderedStates.map(state => {
                const stateKey = getStateKey(state);
                if (!stateKey) return null;

                return (
                  <button
                    key={stateKey}
                    type="button"
                    className={classNames(css.chip, {
                      [css.chipActive]: selectedStateKey === stateKey,
                    })}
                    aria-pressed={selectedStateKey === stateKey}
                    onClick={() => setSelectedStateKey(stateKey)}
                  >
                    {state?.name || 'Unnamed state'}
                  </button>
                );
              })}
            </div>

            {showFilteredEmptyState ? (
              <div className={css.marketplaceEmptyState}>
                <h3>No events found in {selectedState?.name || 'this state'} yet.</h3>
                <p>Try another state or check back soon.</p>
              </div>
            ) : (
              <div className={gridClassName}>
                {filteredEvents.map(event => {
                  if (isCommunityCardVariant) {
                    return (
                      <CommunityEventCard
                        key={event?.id || event?.slug || event?.title}
                        event={event}
                      />
                    );
                  }

                  const canOpenEvent = Boolean(event?.slug);
                  const imageUrl = getStrapiMediaUrl(event?.coverImage);
                  const locationLabel =
                    event?.locationText ||
                    [event?.city || event?.locationCity, event?.stateName || event?.locationState]
                      .filter(Boolean)
                      .join(', ');
                  const eventDateBadge = event?.startDateTime
                    ? moment(event.startDateTime).format('MMM D')
                    : 'TBA';

                  return (
                    <div
                      className={css.eventCard}
                      key={event?.id || event?.slug || event?.title}
                      role={canOpenEvent ? 'link' : undefined}
                      tabIndex={canOpenEvent ? 0 : -1}
                      onClick={() => {
                        if (canOpenEvent) {
                          openEvent(event?.slug);
                        }
                      }}
                      onKeyDown={evt => {
                        if (!canOpenEvent) return;
                        if (evt.key === 'Enter' || evt.key === ' ') {
                          evt.preventDefault();
                          openEvent(event?.slug);
                        }
                      }}
                    >
                      <ImageWithFallback
                        src={imageUrl}
                        alt={event?.title || 'Event cover'}
                        wrapperClassName={css.eventImageWrapper}
                        imageClassName={css.eventCardImage}
                      >
                        <span className={css.eventDateBadge}>{eventDateBadge}</span>
                      </ImageWithFallback>

                      <div className={css.eventBody}>
                        {event?.stateName || event?.locationState ? (
                          <span className={css.instructorStateLabel}>
                            {event?.stateName || event?.locationState}
                          </span>
                        ) : null}
                        <h3 className={css.cardTitle}>{event?.title || 'Event'}</h3>
                        <p className={css.eventDescription}>
                          {event?.shortDescription || 'Event details coming soon.'}
                        </p>
                        <div className={css.eventMeta}>
                          <Icons name="calendarDate" />
                          <span>{formatEventDate(event?.startDateTime, event?.endDateTime)}</span>
                        </div>
                        <div className={css.eventMeta}>
                          <IconLocation className={css.metaIcon} />
                          <span>{locationLabel || 'Location TBA'}</span>
                        </div>
                        <button
                          type="button"
                          className={classNames(css.cardCta, css.eventCta, {
                            [css.cardCtaDisabled]: !canOpenEvent,
                          })}
                          disabled={!canOpenEvent}
                          onClick={evt => {
                            evt.stopPropagation();
                            openEvent(event?.slug);
                          }}
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default SectionPopularCollections;
